import type { NextPage } from 'next';

import Link from 'next/link';

import styles from './../styles/Footer.module.scss';
import Image from 'next/image';

import SundoseLogo from '../public/images/sundose-logo@3x.png';

import AppStoreButton from '../public/images/appstore.png';
import GooglePlayButton from '../public/images/googleplay.png';
import BlikLogo from './../public/images/footer/blik@2x.png';
import PayuLogo from './../public/images/footer/payu@2x.png';
import PaypalLogo from './../public/images/footer/paypal-logo@2x.png';
import VisaLogo from './../public/images/footer/visa@2x.png';
import MastercardLogo from './../public/images/footer/master-card@2x.png';
import DhlLogo from './../public/images/footer/dhl@2x.png';
import FbLogo from './../public/images/fb.svg';
import InstagramLogo from './../public/images/instagram.svg';
import EUProjectsImage from './../public/images/footer/eu-projects-2.png';
import { useTranslation } from 'next-i18next';
import { useRouter } from 'next/router';
import LanguagePicker from './LanguagePicker';
import ProfileIcon from '../public/images/profile.svg';

const CompanyAndAppData: React.FC<{ platform: 'desktop' | 'mobile' }> = ({
  platform
}) => {
  const { t } = useTranslation('common');

  return (
    <div className={`only-${platform} ${styles.companyData}`}>
      <div className={`${styles.appImages} ${styles.pointer}`}>
        <Image
          src={AppStoreButton}
          width={120}
          height={40}
          layout="fixed"
          alt="App Store"
          onClick={() => {
            window.open(
              'https://apps.apple.com/pl/app/sundose/id1464667244',
              '_blank'
            );
          }}
        />
        <Image
          src={GooglePlayButton}
          width={128}
          height={49}
          layout="fixed"
          alt="Google Play"
          onClick={() => {
            window.open(
              'https://play.google.com/store/apps/details?id=com.sundose_mobile&hl=en',
              '_blank'
            );
          }}
        />
      </div>

      <div className={styles.block}>
        <p className={styles.companyDescription}>
          {t('sundose-company-data-1')}
        </p>
        <p className={styles.companyDescription}>
          {t('sundose-company-data-2')}
        </p>
        <p className={styles.companyDescription}>
          {t('sundose-company-data-3')}
        </p>
        <p className={styles.companyDescription}>
          {t('sundose-company-data-4')}
        </p>
        <p className={styles.companyDescription}>
          {t('sundose-company-data-5')}
        </p>
      </div>
    </div>
  );
};

const Footer: NextPage = () => {
  const { t } = useTranslation('common');
  const { locale } = useRouter();

  return (
    <footer className={styles.footer}>
      <div className={`container ${styles.footerContainer}`}>
        <div className={`split ${styles.fullWidth}`}>
          <div className={styles.footerInfo}>
            <Image
              src={SundoseLogo}
              alt="Sundose Logo"
              width={150}
              height={50}
              layout="fixed"
            />
            <p className={styles.description}>{t('footer-description')}</p>

            <CompanyAndAppData platform={'desktop'} />
          </div>

          <div className={styles.footerLinks}>
            <div className={`split ${styles.alignQuarters}`}>
              <div className={`quarter ${styles.one}`}>
                <Link href={`/${locale}/product`}>{t('footer-link-1')}</Link>
                <Link href={`/${locale}/about-us`}>{t('footer-link-2')}</Link>
                <Link href={`/${locale}/contact`}>{t('footer-link-3')}</Link>
                <Link
                  href={`https://sundose.zendesk.com/hc/pl/categories/360002268019-FAQ`}
                >
                  {t('footer-link-4')}
                </Link>
                <Link
                  href={`https://help.sundose.io/hc/pl/articles/360012497019-Dostawa`}
                >
                  {t('footer-link-5')}
                </Link>
                <div className="only-mobile">
                  <Link
                    href={`https://help.sundose.io/hc/pl/articles/360015278959`}
                  >
                    {t('footer-link-6')}
                  </Link>
                  <Link href={`/${locale}/terms/sundose-web-store-rules`}>
                    {t('footer-link-7')}
                  </Link>
                  <Link href={`/${locale}/terms/subscription`}>
                    {t('footer-link-8')}
                  </Link>
                  <Link href={`/${locale}/projects`}>{t('footer-link-9')}</Link>
                  <Link href={`/${locale}/imprint`}>{t('footer-link-10')}</Link>
                  {locale === 'pl' ? (
                    <Link href={`/${locale}/quality`}>Polityka jakości</Link>
                  ) : null}
                </div>
              </div>
              <div className={`quarter ${styles.two} only-desktop`}>
                <Link
                  href={`https://help.sundose.io/hc/pl/articles/360015278959`}
                >
                  {t('footer-link-6')}
                </Link>
                <Link href={`/${locale}/terms/sundose-web-store-rules`}>
                  {t('footer-link-7')}
                </Link>
                <Link href={`/${locale}/terms/subscription`}>
                  {t('footer-link-8')}
                </Link>
                <Link href={`/${locale}/projects`}>{t('footer-link-9')}</Link>
                <Link href={`/${locale}/imprint`}>{t('footer-link-10')}</Link>
                {locale === 'pl' ? (
                  <Link href={`/${locale}/quality`}>Polityka jakości</Link>
                ) : null}
              </div>
              <div className={`quarter ${styles.three} only-desktop`}>
                <LanguagePicker footer />
              </div>
              <div className={`quarter ${styles.four}`}>
                <div className={styles.loginLink}>
                  <Image src={ProfileIcon} alt="Login icon" />
                  <Link locale={false} href={`/app/panel/auth/login/`}>
                    {t('login')}
                  </Link>
                </div>

                <div className="only-mobile">
                  <LanguagePicker footer />
                </div>
              </div>
            </div>
          </div>
        </div>

        <CompanyAndAppData platform={'mobile'} />

        <div className={`split ${styles.imagesContainer}`}>
          <div className={`half ${styles.imagesFirstHalf}`}>
            <span>Made by Sundose © 2021</span>
            <Image
              src={FbLogo}
              alt="Logo"
              onClick={() => {
                window.open(process.env.facebookUrl, '_blank');
              }}
            />
            <Image
              src={InstagramLogo}
              alt="Logo"
              onClick={() => {
                window.open(
                  process.env.instagramBaseUrl + t('instagram-account'),
                  '_blank'
                );
              }}
            />
          </div>
          <div className={`half ${styles.imagesSecondHalf}`}>
            <div className={styles.images}>
              <Image src={BlikLogo} alt="Logo" width={33} height={17} />
              <Image src={PayuLogo} alt="Logo" width={44} height={23} />
              <Image src={PaypalLogo} alt="Logo" width={35} height={35} />
              <Image src={VisaLogo} alt="Logo" width={48} height={17} />
              <Image src={MastercardLogo} alt="Logo" width={33} height={21} />
              <Image src={DhlLogo} alt="Logo" width={51} height={20} />
            </div>
          </div>
        </div>
      </div>
      <div className={`container ${styles.euImageContainer}`}>
        <Image src={EUProjectsImage} alt="EU Projects" />
      </div>
    </footer>
  );
};

export default Footer;
